import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Button,
  Flex,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import CustomerPaginations from "components/pagination/CustomerPaginations";
import { pad_with_zeroes } from "network/helper";
import { Order } from "./types";
import { getOrderStatusInfo } from "constants/statuses";

export function ProjectSelectTable(props: {
  orders: Order[];
  pagedOrders: Order[];
  selectedOrder: Order | null;
  onSelectOrder: (order: Order) => void;
  perPage: number;
  currentPage: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  onStartNewOrder: () => void;
}) {
  const {
    orders,
    pagedOrders,
    selectedOrder,
    onSelectOrder,
    perPage,
    currentPage,
    totalPages,
    onChangePage,
    onStartNewOrder,
  } = props;

  if (orders.length < 1) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH={{ base: "220px", md: "160px" }}
        px="24px"
        py="24px"
      >
        <Text fontSize="16px" color="#0B0E10" mb="6px" textAlign="center">
          No active projects for this service type
        </Text>
        <Text fontSize="14px" color="#6B7280" mb="16px" textAlign="center">
          Create a new order to add this service.
        </Text>
        <Button
          bgColor="#3A7BFF"
          color="#FFFFFF"
          fontSize="14px"
          fontWeight="500"
          h="32px"
          borderRadius="25px"
          border="1px solid #3A7BFF"
          _hover={{ bgColor: "#FFFFFF", color: "#3A7BFF" }}
          _active={{ bgColor: "#FFFFFF", color: "#3A7BFF" }}
          onClick={onStartNewOrder}
        >
          Create new order
        </Button>
      </Flex>
    );
  }

  return (
    <Box maxW={{ md: "unset", base: "calc(100vw - 100px)" }} overflow="auto" p={{ base: "0px", md: "0px" }}>
      <Table fontSize="14px">
        <Thead>
          <Tr>
            <Th
              padding="12px 16px"
              fontSize="14px"
              fontWeight="600"
              color="#0B0E10"
              textTransform="none"
              borderBottom="1px solid #E5E7EB"
            >
              Project #
            </Th>
            <Th
              padding="12px 16px"
              fontSize="14px"
              fontWeight="600"
              color="#0B0E10"
              textTransform="none"
              borderBottom="1px solid #E5E7EB"
            >
              Status
            </Th>
            <Th
              padding="12px 16px"
              fontSize="14px"
              fontWeight="600"
              color="#0B0E10"
              textTransform="none"
              borderBottom="1px solid #E5E7EB"
            >
              Type
            </Th>
            <Th
              padding="12px 16px"
              fontSize="14px"
              fontWeight="600"
              color="#0B0E10"
              textTransform="none"
              borderBottom="1px solid #E5E7EB"
            >
              Due Date
            </Th>
            <Th
              padding="12px 16px"
              fontSize="14px"
              fontWeight="600"
              color="#0B0E10"
              textTransform="none"
              borderBottom="1px solid #E5E7EB"
            >
              Price
            </Th>
            <Th padding="12px 16px" borderBottom="1px solid #E5E7EB" width="50px" />
          </Tr>
        </Thead>
        <Tbody>
          {pagedOrders.map((it, index) => {
            const isSelected = String(selectedOrder?.order_id) === String(it?.order_id);
            return (
              <Tr
                key={index}
                cursor="pointer"
                bg={isSelected ? "#E8EDF2" : "transparent"}
                _hover={{ bg: "#E8EDF2" }}
                onClick={() => onSelectOrder(it)}
              >
                <Td
                  padding="12px"
                  fontSize="16px"
                  color="#111827"
                  borderBottom="1px solid #E6ECF3"
                >
                  #{pad_with_zeroes(String(it?.order_id ?? ""), 4)}
                </Td>
                <Td padding="12px" borderBottom="1px solid #E6ECF3">
                  <Badge
                    bg={getOrderStatusInfo(it?.order_status_id).bg}
                    color={getOrderStatusInfo(it?.order_status_id).text}
                    borderRadius="4px"
                    px="5px 8px"
                    fontSize="16px"
                    fontWeight="400"
                    textTransform="none"
                  >
                    {getOrderStatusInfo(it?.order_status_id).name}
                  </Badge>
                </Td>
                <Td
                  padding="12px"
                  fontSize="16px"
                  color="#0B0E10"
                  borderBottom="1px solid #E6ECF3"
                >
                  {it?.translation_type
                    ? String(it.translation_type).charAt(0).toUpperCase() +
                      String(it.translation_type).slice(1)
                    : "-"}
                </Td>
                <Td
                  padding="12px"
                  fontSize="16px"
                  fontWeight="400"
                  color="#0B0E10"
                  borderBottom="1px solid #E6ECF3"
                >
                  {it?.order_due_at
                    ? (() => {
                        const d = new Date(String(it.order_due_at));
                        return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
                      })()
                    : "-"}
                </Td>
                <Td
                  padding="12px"
                  fontSize="16px"
                  color="#0B0E10"
                  fontWeight="400"
                  borderBottom="1px solid #E6ECF3"
                >
                  ${String(it?.order_total || "0.00")}
                </Td>
                <Td padding="12px" borderBottom="1px solid #E6ECF3" textAlign="right">
                  <IconButton
                    aria-label="Select"
                    icon={<ChevronRightIcon w="24px" h="24px" />}
                    variant="ghost"
                    size="sm"
                    _hover={{ bg: "transparent" }}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {orders.length > perPage && (
        <Box px="16px" py="16px" bg="#F5F7FA">
          <CustomerPaginations
            totalCount={orders.length}
            totalPages={totalPages}
            currentPage={currentPage}
            perPage={perPage}
            onChangePage={onChangePage}
            showPageSizeSelector={false}
          />
        </Box>
      )}
    </Box>
  );
}
